import { Users } from "lucide-react";
import { overloadLevel } from "../../utils/assignmentStorage";

const ROLE_LABELS = {
  IRBAN: "Irban",
  DALNIS: "Dalnis",
  KETUA_TIM: "Ketua Tim",
  ANGGOTA_TIM: "Anggota Tim",
  ADMIN: "Admin",
};

const LEVEL_CONFIG = {
  HIGH: {
    bar: "bg-red-500",
    avatar: "bg-red-100 text-red-700",
    badge: "bg-red-50 text-red-600 border-red-100",
    label: "Tinggi",
    dot: "bg-red-500",
  },
  MEDIUM: {
    bar: "bg-amber-400",
    avatar: "bg-amber-100 text-amber-700",
    badge: "bg-amber-50 text-amber-600 border-amber-100",
    label: "Sedang",
    dot: "bg-amber-400",
  },
  OK: {
    bar: "bg-emerald-500",
    avatar: "bg-blue-50 text-[#1e3a5f]",
    badge: "bg-emerald-50 text-emerald-600 border-emerald-100",
    label: "Normal",
    dot: "bg-emerald-500",
  },
};

function shortName(name) {
  if (!name) return "—";
  const base = name.split(",")[0];
  return base.length > 22 ? `${base.slice(0, 20)}…` : base;
}

function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
}

function buildData(people, scopedSubs, assignments) {
  return people
    .map((u) => {
      const active = scopedSubs.filter((s) => {
        if (s.status === "SELESAI") return false;
        const a = assignments[s.id];
        if (!a) return false;
        return (
          a.dalnisId === u.id ||
          a.ketuaId === u.id ||
          (Array.isArray(a.anggotaIds) && a.anggotaIds.includes(u.id))
        );
      }).length;
      return { id: u.id, name: u.name, role: u.role, total: active };
    })
    .sort((a, b) => b.total - a.total);
}

// Max reference for bar width (at least 10 or max among team)
function maxRef(data) {
  const m = Math.max(...data.map((d) => d.total), 10);
  return m;
}

export default function BebanTimChart({ people, scopedSubs, assignments }) {
  const data = buildData(people, scopedSubs, assignments);
  const max = maxRef(data);

  const highCount = data.filter((d) => overloadLevel(d.total) === "HIGH").length;
  const medCount = data.filter((d) => overloadLevel(d.total) === "MEDIUM").length;
  const okCount = data.filter((d) => overloadLevel(d.total) === "OK").length;

  return (
    <div
      data-testid="chart-beban-tim"
      className="rounded-2xl border border-violet-100/60 p-5 shadow-sm print:shadow-none relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #faf5ff 0%, #ffffff 55%)" }}
    >
      {/* Colored header band */}
      <div
        className="-mx-5 -mt-5 px-5 py-4 mb-4 rounded-t-2xl"
        style={{ background: "linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20">
              <Users className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-white leading-tight">
                Beban Tim
              </h3>
              <p className="text-[11px] text-violet-200 mt-0.5">
                Kegiatan aktif per personel
              </p>
            </div>
          </div>
          <span className="text-[11px] font-semibold text-white bg-white/20 px-2.5 py-1 rounded-full">
            {data.length} personel
          </span>
        </div>
      </div>

      {/* Legend chips */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { key: "HIGH", count: highCount },
          { key: "MEDIUM", count: medCount },
          { key: "OK", count: okCount },
        ].map(({ key, count }) => (
          <div
            key={key}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${LEVEL_CONFIG[key].badge}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${LEVEL_CONFIG[key].dot}`} />
            {LEVEL_CONFIG[key].label}: {count}
          </div>
        ))}
      </div>

      {/* Note */}
      <p className="text-[10.5px] text-slate-400 mb-3">
        🔴 &gt;7 kegiatan &nbsp;·&nbsp; 🟡 &gt;5 kegiatan &nbsp;·&nbsp; 🟢 ≤5 kegiatan
      </p>

      {/* Team list */}
      <div className="space-y-3">
        {data.length === 0 ? (
          <div className="flex h-24 items-center justify-center text-[12px] italic text-slate-400">
            Tidak ada data beban tim
          </div>
        ) : (
          data.map((d, idx) => {
            const level = overloadLevel(d.total);
            const cfg = LEVEL_CONFIG[level];
            const barW = max > 0 ? Math.round((d.total / max) * 100) : 0;

            return (
              <div key={d.id} className="flex items-center gap-3 group">
                {/* Rank */}
                <div className="w-5 text-[11px] font-bold text-slate-300 text-center flex-shrink-0">
                  {idx + 1}
                </div>

                {/* Avatar */}
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0 ${cfg.avatar}`}
                  title={d.name}
                >
                  {initials(d.name)}
                </div>

                {/* Name + bar */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <span className="text-[12px] font-semibold text-slate-800 truncate block max-w-[120px]">
                        {shortName(d.name)}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {ROLE_LABELS[d.role] || d.role}
                      </span>
                    </div>
                    <span className={`ml-2 flex-shrink-0 px-2 py-0.5 rounded-full border text-[11px] font-bold ${cfg.badge}`}>
                      {d.total}
                    </span>
                  </div>
                  {/* Bar */}
                  <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${cfg.bar}`}
                      style={{ width: `${barW}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
